import React, { useState, useEffect, useCallback, useRef } from "react";
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { getPersonalList, updateItem, deleteItem, clearCheckedPersonal, reorderItems } from "../api";
import CategoryGroup from "./CategoryGroup";
import SortableItem from "./SortableItem";
import Toast from "./Toast";

/**
 * Liste personnelle — mes articles groupés par catégorie.
 * Permet de cocher (acheté), modifier et supprimer ses propres articles.
 */
function PersonalList({ onRefresh, categories }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [reorderMode, setReorderMode] = useState(false);
  const deleteTimerRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
  );

  const fetchItems = async (pageNum = 1, append = false) => {
    try {
      const data = await getPersonalList(pageNum);
      if (append) {
        setItems((prev) => [...prev, ...data.items]);
      } else {
        setItems(data.items);
      }
      setTotalItems(data.total);
      setHasMore(pageNum < data.pages);
      setPage(pageNum);
    } catch (err) {
      console.error("Erreur chargement liste perso:", err);
      if (!append) {
        setItems([]);
        setTotalItems(0);
        setHasMore(false);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const handleLoadMore = () => {
    fetchItems(page + 1, true);
  };

  const handleCheck = async (item) => {
    try {
      await updateItem(item.id, { checked: !item.checked });
      fetchItems();
      onRefresh?.();
    } catch (err) {
      console.error("Erreur mise à jour:", err);
    }
  };

  const handleEdit = async (id, data) => {
    await updateItem(id, data);
    fetchItems();
    onRefresh?.();
  };

  const handleDelete = useCallback(async (id) => {
    const deletedItem = items.find((i) => i.id === id);
    if (!deletedItem) return;

    setItems((prev) => prev.filter((i) => i.id !== id));

    try {
      await deleteItem(id);
      onRefresh?.();
    } catch (err) {
      console.error("Erreur suppression:", err);
      setItems((prev) => [...prev, deletedItem].sort((a, b) => a.id - b.id));
      return;
    }

    setToast({
      message: `"${deletedItem.name}" supprimé`,
      onUndo: async () => {
        try {
          const { addItem } = await import("../api");
          await addItem({
            name: deletedItem.name,
            quantity: deletedItem.quantity,
            category: deletedItem.category,
          });
          fetchItems();
          onRefresh?.();
        } catch (err) {
          console.error("Erreur annulation suppression:", err);
        }
        setToast(null);
      },
      onExpire: () => {
        setToast(null);
      },
    });
  }, [items, onRefresh]);

  const handleClearChecked = async (e) => {
    e.preventDefault();
    const checkedCount = items.filter((i) => i.checked).length;
    if (!window.confirm(`Supprimer les ${checkedCount} articles cochés ?`)) return;
    await clearCheckedPersonal();
    fetchItems();
    onRefresh?.();
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const unchecked = items.filter((i) => !i.checked);
    const checked = items.filter((i) => i.checked);
    const oldIndex = unchecked.findIndex((i) => i.id === active.id);
    const newIndex = unchecked.findIndex((i) => i.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(unchecked, oldIndex, newIndex);
    setItems([...reordered, ...checked]);

    const order = reordered.map((item, idx) => ({ id: item.id, sort_order: idx }));
    try {
      await reorderItems(order);
      onRefresh?.();
    } catch (err) {
      console.error("Erreur réordonnement:", err);
      fetchItems();
    }
  };

  if (loading) return <p className="loading-text">Chargement...</p>;

  const unchecked = items.filter((i) => !i.checked);
  const checked = items.filter((i) => i.checked);

  const grouped = {};
  unchecked.forEach((item) => {
    const cat = item.category || "Autre";
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(item);
  });

  if (items.length === 0) {
    return (
      <div className="empty-state">
        <span className="empty-icon">&#x1F3D7;</span>
        <p>Ta liste est vide !</p>
        <p className="empty-hint">Appuie sur + pour ajouter un article</p>
      </div>
    );
  }

  return (
    <div className="item-list">
      {unchecked.length > 1 && (
        <button
          className={`btn-reorder-toggle ${reorderMode ? "active" : ""}`}
          onClick={() => setReorderMode(!reorderMode)}
        >
          {reorderMode ? "✓ Terminé" : "↕ Réordonner"}
        </button>
      )}

      {reorderMode ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={unchecked.map((i) => i.id)} strategy={verticalListSortingStrategy}>
            <div className="category-group reorder-list">
              <ul className="category-items">
                {unchecked.map((item) => (
                  <SortableItem
                    key={item.id}
                    item={item}
                    onCheck={handleCheck}
                    onDelete={handleDelete}
                    showOwner={false}
                  />
                ))}
              </ul>
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        Object.entries(grouped).map(([category, catItems]) => (
          <CategoryGroup
            key={category}
            category={category}
            items={catItems}
            onCheck={handleCheck}
            onDelete={handleDelete}
            onEdit={handleEdit}
            showOwner={false}
            categories={categories}
          />
        ))
      )}

      {checked.length > 0 && (
        <details className="checked-section">
          <summary className="checked-header">
            &#x2705; Terminé ({checked.length})
            <button
              className="btn-clear-checked"
              onClick={handleClearChecked}
              title="Supprimer tous les articles cochés"
            >
              &#x1F5D1; Tout vider
            </button>
          </summary>
          <ul className="category-items">
            {checked.map((item) => (
              <li key={item.id} className="shopping-item checked">
                <button className="item-checkbox" onClick={() => handleCheck(item)}>
                  &#x2705;
                </button>
                <div className="item-info">
                  <span className="item-name">{item.name}</span>
                </div>
                <button className="item-delete" onClick={() => handleDelete(item.id)}>
                  &#x1F5D1;
                </button>
              </li>
            ))}
          </ul>
        </details>
      )}

      {hasMore && (
        <button className="btn-load-more" onClick={handleLoadMore}>
          Charger plus ({items.length}/{totalItems})
        </button>
      )}

      {toast && (
        <Toast
          message={toast.message}
          onUndo={toast.onUndo}
          onExpire={toast.onExpire}
        />
      )}
    </div>
  );
}

export default PersonalList;
